import { SpriteIcon } from '../sprite-icon';
import type { ASSET_SPRITE_MAPPING, SPRITE_MAPPING } from '../sprite-mapping';
import type { Direction, SVGProps } from '../types';

type ProductIconName = (typeof SPRITE_MAPPING)[keyof typeof SPRITE_MAPPING];

// build-sprite.mjs namespaces asset symbols with `asset-` when merging into
// each app's filtered sprite, so consumers reference them as `asset-<id>`.
type AssetIconName =
  `asset-${(typeof ASSET_SPRITE_MAPPING)[keyof typeof ASSET_SPRITE_MAPPING]}`;

/**
 * Union of every sprite id reachable through the per-app filtered sprite
 * (`dist/sprite.svg`). Derived from `SPRITE_MAPPING` + `ASSET_SPRITE_MAPPING`,
 * so it tracks the actually-shipped catalogue rather than the full upstream
 * design-system manifest (~722 ids, most of which aren't bundled).
 */
export type IconName = ProductIconName | AssetIconName;

type IconProps = Omit<SVGProps, 'direction'> & {
  /** Sprite id reachable via the per-app sprite (e.g. `"bell"`, `"asset-dapp-maple"`). */
  name: IconName;
  /** Rotates the whole svg from the design-system baseline (e.g. chevron points left). */
  direction?: Direction;
};

/**
 * Sprite-backed icon for new code. Renders `<svg><use href="...#name" /></svg>`
 * against the shared sprite. Prefer it over the named components: roughly half
 * of those still ship their own inline `<svg>` instead.
 *
 * Decorative by default: with no `title` the svg is `aria-hidden` and announces
 * nothing. An icon that carries meaning on its own — an icon-only button, a
 * status glyph with no adjacent text — must pass `title` explicitly.
 *
 * @example <Icon name="search" className="size-4 text-red-500" />
 * @example <Icon name="chevron-left" direction="down" />
 * @example <Icon name="alert-triangle" title="Action required" />
 */
export const Icon = ({ name, direction, ...rest }: IconProps) => (
  <SpriteIcon spriteId={name} rotation={direction} {...rest} />
);
